import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  isTransientCursorError,
  useSafeLiveQuery,
} from "@/indexedDB/hooks/useSafeLiveQuery";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { debounce } from "lodash";
// Month formatting removed as we no longer group by month
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
// LinkEntity type is used in the returned linkAttachments array
import type { LinkEntity } from "@/indexedDB/types";
import {
  getPaginatedLinksByChatIdFromLocalDB,
  getLinkCountByChatIdFromLocalDB,
} from "@/indexedDB/services/links";

const LOADING_DELAY = 2000;

/**
 * Custom hook for managing links
 * Handles loading, pagination, and grouping of links
 *
 * How it works:
 * 1. Fetches links from IndexedDB with pagination
 * 2. Groups links by month for organized display
 * 3. Implements infinite scrolling with intersection observer
 * 4. Manages loading and error states
 *
 * Key features:
 * - Infinite scrolling with intersection observer
 * - Loading and error states with debounced loading
 * - Links grouped by month
 * - Pagination with automatic loading
 */
export function useLinks(activeChatId: string | null) {
  // State for pagination and UI states
  const [linksTake, setLinksTake] = useState(MESSAGE_LIST_PAGE_SIZE);
  const [isFullLinkListLoaded, setIsFullLinkListLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Create a debounced version of setIsLoading(false) with a 2-second delay
  // to prevent loading state flickering
  const debouncedSetLoadingFalse = useMemo(
    () => debounce(() => setIsLoading(false), LOADING_DELAY),
    [],
  );

  // Enhanced setIsLoading function that handles overlapping state changes
  const setLoadingState = useCallback(
    (isLoadingState: boolean) => {
      if (isLoadingState) {
        // Cancel any pending "set to false" operations when setting to true
        debouncedSetLoadingFalse.cancel();
        setIsLoading(true);
      } else {
        // Use the debounced version when setting to false
        debouncedSetLoadingFalse();
      }
    },
    [debouncedSetLoadingFalse],
  );

  // Refs for scroll handling
  const scrollerRef = useRef<HTMLDivElement>(null);
  const linksListRef = useRef<HTMLDivElement>(null);

  // Intersection observer for top anchor (loading more links)
  const [anchorRef, topAnchorEntry] = useIntersectionObserver({
    threshold: 0,
    root: scrollerRef.current,
    rootMargin: "0px",
  });

  // Live query links from IndexedDB
  // Updates automatically when links change
  const linkAttachments = useSafeLiveQuery(
    async () => {
      if (!activeChatId) {
        return [];
      }

      try {
        setLoadingState(true);
        const result = await getPaginatedLinksByChatIdFromLocalDB(
          activeChatId,
          linksTake,
        );
        setLoadingState(false);
        return result;
      } catch (err) {
        setLoadingState(false);
        // Let the known WebKit cursor error reach useSafeLiveQuery so it can
        // serve last-good + force a fresh subscription; only surface other
        // errors as a tab-level UI error.
        if (isTransientCursorError(err)) {
          throw err;
        }
        setError(err as Error);
        return [];
      }
    },
    [activeChatId, linksTake],
    [],
  );

  // Get total count of links for pagination
  const linkAttachmentsCount = useSafeLiveQuery(
    () => (activeChatId ? getLinkCountByChatIdFromLocalDB(activeChatId) : 0),
    [activeChatId],
    0,
  );

  // Month timestamp function removed as we no longer group by month

  // We no longer need to group links by month
  // Links are already sorted by timestamp (newest first) from the database query

  // Load more links when scrolling to the top
  const loadMoreLinks = useCallback(() => {
    if (!linksListRef.current) {
      return;
    }
    if (!activeChatId) {
      return;
    }
    if (linkAttachments.length === 0) {
      return;
    }
    if (!linkAttachmentsCount || linkAttachmentsCount === 0) {
      return;
    }
    if (!linksTake) {
      return;
    }

    // Check if we've loaded all links
    if (linksTake >= linkAttachmentsCount) {
      setIsFullLinkListLoaded(true);
      return;
    }

    // Load more links
    if (linksTake < linkAttachmentsCount) {
      setLinksTake(linksTake + MESSAGE_LIST_PAGE_SIZE);
    }
  }, [
    activeChatId,
    linkAttachments.length,
    linkAttachmentsCount,
    linksTake,
    linksListRef,
  ]);

  // Create a debounced version of loadMoreLinks for better performance
  const debouncedLoadMore = useMemo(() => {
    return debounce(() => {
      if (topAnchorEntry?.isIntersecting) {
        loadMoreLinks();
      }
    }, 200);
  }, [topAnchorEntry, loadMoreLinks]);

  // Load more links when top anchor is visible
  useEffect(() => {
    debouncedLoadMore();
    return () => debouncedLoadMore.cancel();
  }, [debouncedLoadMore]);

  // Handle retry loading
  const handleRetry = useCallback(() => {
    if (!activeChatId) {
      return;
    }

    setError(null);
    setLoadingState(true);

    getPaginatedLinksByChatIdFromLocalDB(activeChatId, linksTake)
      .then(() => {
        setLoadingState(false);
      })
      .catch((err) => {
        setLoadingState(false);
        setError(err as Error);
      });
  }, [activeChatId, linksTake, setLoadingState]);

  // Cancel any pending debounced calls when unmounting
  useEffect(() => {
    return () => debouncedSetLoadingFalse.cancel();
  }, [debouncedSetLoadingFalse]);

  // Explicitly type the return value to use LinkEntity
  return {
    isLoading,
    error,
    linkAttachments, // This is LinkEntity[]
    isFullLinkListLoaded,
    scrollerRef,
    linksListRef,
    anchorRef,
    handleRetry,
  } as {
    isLoading: boolean;
    error: Error | null;
    linkAttachments: LinkEntity[];
    isFullLinkListLoaded: boolean;
    scrollerRef: React.RefObject<HTMLDivElement>;
    linksListRef: React.RefObject<HTMLDivElement>;
    anchorRef: (node: Element | null) => void;
    handleRetry: () => void;
  };
}
